import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  Alert,
} from "reactstrap";
import Swal from "sweetalert2";
import {
  getEmployerCustomPricing,
  updateEmployerCustomPricing,
  removeEmployerCustomPricing,
} from "../../../slices/admin/thunk";
import { formatCentsToDollars } from "../../../utils/utils";
import { DASHBOARD_USER_ROLES } from "../../../constants";

const EmployerCustomPricingModal = ({ isOpen, setIsOpen, employerProfile, currentUser, onRefresh }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);

  // Admin access validation
  const isCurrentUserAdmin = currentUser?.role === DASHBOARD_USER_ROLES.ADMIN;

  // Current pricing state
  const [currentPricing, setCurrentPricing] = useState(null);

  // Form states
  const [perEmployeeAmount, setPerEmployeeAmount] = useState("");
  const [perFamilyAmount, setPerFamilyAmount] = useState("");

  // Validation states
  const [perEmployeeError, setPerEmployeeError] = useState("");
  const [perFamilyError, setPerFamilyError] = useState("");
  const [formError, setFormError] = useState("");

  const resetModal = () => {
    setCurrentPricing(null);
    setPerEmployeeAmount("");
    setPerFamilyAmount("");
    setPerEmployeeError("");
    setPerFamilyError("");
    setFormError("");
  };

  const handleClose = () => {
    resetModal();
    setIsOpen(false);
  };

  const fetchPricingData = async () => {
    if (!employerProfile?.id) return;

    try {
      setFetchingData(true);

      const pricingResponse = await dispatch(getEmployerCustomPricing({ employerId: employerProfile.id }));
      // Handle both direct response and nested data response
      const data = pricingResponse.payload?.data || pricingResponse.payload;

      if (data) {
        setCurrentPricing(data);

        // Pre-fill form if custom pricing exists (use != null to handle 0 values)
        if (data.customPerEmployeeAmount != null) {
          setPerEmployeeAmount(data.customPerEmployeeAmount.toString());
        }
        if (data.customPerFamilyEmployeeAmount != null) {
          setPerFamilyAmount(data.customPerFamilyEmployeeAmount.toString());
        }
      }
    } catch (error) {
      console.error("Error fetching pricing data:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to load pricing data",
        icon: "error",
      });
    } finally {
      setFetchingData(false);
    }
  };

  useEffect(() => {
    if (isOpen && employerProfile) {
      resetModal();
      fetchPricingData();
    }
  }, [isOpen, employerProfile]);

  // Get employer display name
  const employerName = employerProfile?.companyName ||
    `${employerProfile?.firstName || ""} ${employerProfile?.lastName || ""}`.trim() ||
    "Employer";

  const validateForm = () => {
    let isValid = true;
    setPerEmployeeError("");
    setPerFamilyError("");
    setFormError("");

    const perEmployee = parseInt(perEmployeeAmount);
    const perFamily = parseInt(perFamilyAmount);

    // Validate per employee amount (in cents)
    if (!perEmployeeAmount || isNaN(perEmployee) || perEmployee < 100 || perEmployee > 10000) {
      setPerEmployeeError("Per Employee amount must be between 100 and 10000 cents ($1.00 - $100.00)");
      isValid = false;
    }

    // Validate per family amount (in cents)
    if (!perFamilyAmount || isNaN(perFamily) || perFamily < 100 || perFamily > 15000) {
      setPerFamilyError("Per Family amount must be between 100 and 15000 cents ($1.00 - $150.00)");
      isValid = false;
    }

    // Validate relationship between amounts
    if (isValid && perFamily < perEmployee) {
      setFormError("Per Family amount must be greater than or equal to Per Employee amount");
      isValid = false;
    }

    return isValid;
  };

  const handleUpdatePricing = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      // Values are already in cents
      const perEmployeeCents = parseInt(perEmployeeAmount);
      const perFamilyCents = parseInt(perFamilyAmount);

      const result = await dispatch(updateEmployerCustomPricing({
        employerId: employerProfile.id,
        customPerEmployeeAmount: perEmployeeCents,
        customPerFamilyEmployeeAmount: perFamilyCents,
      }));

      if (result.error) {
        throw new Error(result.error.message || "Failed to update custom pricing");
      }

      Swal.fire({
        title: "Success!",
        text: "Custom pricing updated successfully",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });

      // Refresh data
      await fetchPricingData();
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error("Error updating custom pricing:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to update custom pricing",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePricing = async () => {
    const result = await Swal.fire({
      title: "Remove Custom Pricing?",
      text: "This will remove all custom pricing for this employer and revert to default pricing.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove it!",
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);

      await dispatch(removeEmployerCustomPricing({ employerId: employerProfile.id }));

      Swal.fire({
        title: "Removed!",
        text: "Custom pricing has been removed successfully",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });

      // Reset form and refresh data
      setPerEmployeeAmount("");
      setPerFamilyAmount("");
      setCurrentPricing(null);
      await fetchPricingData();
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error("Error removing custom pricing:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to remove custom pricing",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Security check: Only allow admin users
  if (!isCurrentUserAdmin) {
    return null;
  }

  if (!employerProfile) {
    return null;
  }

  const hasCustomPricing = currentPricing?.customPerEmployeeAmount != null || currentPricing?.customPerFamilyEmployeeAmount != null;

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>
        Custom Pricing for {employerName}
      </ModalHeader>
      <ModalBody>
        {fetchingData ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2">Loading pricing data...</p>
          </div>
        ) : (
          <>
            <Row>
              <Col md={6}>
                <Card className="mb-3">
                  <CardHeader>
                    <h6 className="mb-0">Employer Information</h6>
                  </CardHeader>
                  <CardBody>
                    <p><strong>Company:</strong> {employerName}</p>
                    <p><strong>Email:</strong> {employerProfile.email}</p>
                    <p>
                      <strong>Custom Pricing Status:</strong>
                      <span className={`ms-2 badge ${hasCustomPricing ? 'bg-success' : 'bg-secondary'}`}>
                        {hasCustomPricing ? 'Enabled' : 'Default'}
                      </span>
                    </p>
                  </CardBody>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="mb-3">
                  <CardHeader>
                    <h6 className="mb-0">Current Pricing</h6>
                  </CardHeader>
                  <CardBody>
                    {hasCustomPricing ? (
                      <div>
                        <p>
                          <strong>Per Employee:</strong>
                          <span className="ms-2">
                            {currentPricing.customPerEmployeeAmount} cents
                            (${formatCentsToDollars(currentPricing.customPerEmployeeAmount)})
                          </span>
                        </p>
                        <p>
                          <strong>Per Family Employee:</strong>
                          <span className="ms-2">
                            {currentPricing.customPerFamilyEmployeeAmount} cents
                            (${formatCentsToDollars(currentPricing.customPerFamilyEmployeeAmount)})
                          </span>
                        </p>
                      </div>
                    ) : (
                      <p className="text-muted">Using default pricing structure</p>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Card>
                  <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">
                        {hasCustomPricing ? 'Update Custom Pricing' : 'Set Custom Pricing'}
                      </h6>
                      {hasCustomPricing && (
                        <Button
                          color="danger"
                          size="sm"
                          onClick={handleRemovePricing}
                          disabled={loading}
                        >
                          <i className="ri-delete-bin-line me-1"></i>
                          Remove Custom Pricing
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardBody>
                    <Form>
                      <Row>
                        <Col md={6}>
                          <FormGroup>
                            <Label>Per Employee Amount (cents) *</Label>
                            <Input
                              type="number"
                              step="1"
                              min="100"
                              max="10000"
                              value={perEmployeeAmount}
                              onChange={(e) => {
                                setPerEmployeeAmount(e.target.value);
                                if (perEmployeeError) setPerEmployeeError("");
                                if (formError) setFormError("");
                              }}
                              invalid={!!perEmployeeError}
                            />
                            {perEmployeeError && <div className="invalid-feedback">{perEmployeeError}</div>}
                            <small className="text-muted">
                              Range: 100 - 10000 cents ($1.00 - $100.00). Example: 1500 = $15.00
                            </small>
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label>Per Family Employee Amount (cents) *</Label>
                            <Input
                              type="number"
                              step="1"
                              min="100"
                              max="15000"
                              value={perFamilyAmount}
                              onChange={(e) => {
                                setPerFamilyAmount(e.target.value);
                                if (perFamilyError) setPerFamilyError("");
                                if (formError) setFormError("");
                              }}
                              invalid={!!perFamilyError}
                            />
                            {perFamilyError && <div className="invalid-feedback">{perFamilyError}</div>}
                            <small className="text-muted">
                              Range: 100 - 15000 cents ($1.00 - $150.00). Must be &ge; per employee amount.
                            </small>
                          </FormGroup>
                        </Col>
                      </Row>

                      {formError && (
                        <Alert color="danger" className="py-2 mt-3">
                          {formError}
                        </Alert>
                      )}

                      <div className="mt-4">
                        <Button
                          color="primary"
                          onClick={handleUpdatePricing}
                          disabled={loading || !perEmployeeAmount || !perFamilyAmount}
                          block
                        >
                          {loading ? <Spinner size="sm" className="me-1" /> : null}
                          {hasCustomPricing ? 'Update Custom Pricing' : 'Set Custom Pricing'}
                        </Button>
                      </div>
                    </Form>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={loading}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default EmployerCustomPricingModal;
